import styled from "@emotion/styled";
import { theme } from "../lib/theme";
import { useStorageOverview } from "../api/hooks";
import { Loading } from "./ui";
import type { TableStorageEntry } from "@clustec/common";

function formatBytes(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)} GB`;
  if (n >= 1e6) return `${Math.round(n / 1e6)} MB`;
  if (n >= 1e3) return `${Math.round(n / 1e3)} KB`;
  return `${n} B`;
}

function pct(part: number, total: number): string {
  if (total === 0) return "0%";
  return `${((part / total) * 100).toFixed(1)}%`;
}

// ── Category config ──────────────────────────────────────────────────────────

type Category = TableStorageEntry["category"];

const CATEGORY_LABEL: Record<Category, string> = {
  transactions_archival:   "Raw / Archival",
  transactions_processed:  "Transactions (indexed)",
  analysis_output:         "Analysis Output",
  side_effects:            "Side Effects",
  metadata:                "Metadata",
};

const CATEGORY_COLOR: Record<Category, string> = {
  transactions_archival:  theme.colors.warning,
  transactions_processed: theme.colors.primary,
  analysis_output:        theme.colors.accent,
  side_effects:           theme.colors.success,
  metadata:               theme.colors.textMuted,
};

const CATEGORY_ORDER: Category[] = [
  "transactions_archival",
  "transactions_processed",
  "analysis_output",
  "side_effects",
  "metadata",
];

// ── Styled components ────────────────────────────────────────────────────────

const StackedBar = styled.div`
  display: flex;
  height: 18px;
  border-radius: ${theme.radius.sm};
  overflow: hidden;
  gap: 1px;
  background: ${theme.colors.bg};
`;

const BarSegment = styled.div<{ color: string; widthPct: number }>`
  background: ${(p) => p.color};
  flex: 0 0 max(${(p) => p.widthPct}%, 2px);
  transition: opacity 0.15s;
  cursor: default;
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto auto auto;
  gap: 0;
  font-size: ${theme.fontSize.xs};
`;

const GridHeader = styled.div`
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.textMuted};
  padding: 6px 8px 4px;
  border-bottom: 1px solid ${theme.colors.border};
  text-align: left;
`;

const GridSectionHeader = styled.div`
  grid-column: 1 / -1;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${theme.colors.textMuted};
  background: ${theme.colors.bgHover};
  padding: 4px 8px;
  margin-top: 2px;
`;

const GridCell = styled.div<{ right?: boolean; muted?: boolean }>`
  padding: 5px 8px;
  font-family: "SF Mono", "Fira Code", monospace;
  color: ${(p) => (p.muted ? theme.colors.textMuted : theme.colors.text)};
  border-bottom: 1px solid ${theme.colors.border}22;
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.right ? "flex-end" : "flex-start")};
  gap: 6px;
`;

const Dot = styled.span<{ color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.color};
  flex-shrink: 0;
`;

const InlineBar = styled.div<{ widthPct: number; color: string }>`
  width: 60px;
  height: 5px;
  border-radius: 2px;
  background: ${theme.colors.bgHover};
  position: relative;
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    border-radius: 2px;
    width: ${(p) => Math.max(p.widthPct, 2)}%;
    background: ${(p) => p.color};
  }
`;

const Tag = styled.span<{ variant: "archival" | "operational" | "neutral" }>`
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 1px 5px;
  border-radius: 3px;
  background: ${(p) =>
    p.variant === "archival"
      ? `${theme.colors.warning}22`
      : p.variant === "operational"
        ? `${theme.colors.primary}22`
        : theme.colors.bgHover};
  color: ${(p) =>
    p.variant === "archival"
      ? theme.colors.warning
      : p.variant === "operational"
        ? theme.colors.primary
        : theme.colors.textMuted};
`;

const Freshness = styled.div`
  font-size: 9px;
  color: ${theme.colors.textMuted};
  text-align: right;
  margin-top: 2px;
`;

// ── Component ────────────────────────────────────────────────────────────────

export function StorageOverview() {
  const { data, isLoading } = useStorageOverview();

  if (isLoading) return <Loading />;
  if (!data) return null;

  const { tables, totalBytes, generatedAt } = data;

  // Group tables by category in display order
  const byCategory = new Map<Category, typeof tables>();
  for (const cat of CATEGORY_ORDER) byCategory.set(cat, []);
  for (const t of tables) {
    byCategory.get(t.category as Category)?.push(t);
  }

  // Totals per category for stacked bar
  const categoryTotals = new Map<Category, number>();
  for (const cat of CATEGORY_ORDER) {
    const sum = (byCategory.get(cat) ?? []).reduce((s, t) => s + t.totalBytes, 0);
    categoryTotals.set(cat, sum);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing.md }}>

      {/* Stacked summary bar */}
      <StackedBar title={`Total: ${formatBytes(totalBytes)}`}>
        {CATEGORY_ORDER.map((cat) => {
          const bytes = categoryTotals.get(cat) ?? 0;
          const widthPct = totalBytes > 0 ? (bytes / totalBytes) * 100 : 0;
          return (
            <BarSegment
              key={cat}
              color={CATEGORY_COLOR[cat]}
              widthPct={widthPct}
              title={`${CATEGORY_LABEL[cat]}: ${formatBytes(bytes)} (${pct(bytes, totalBytes)})`}
            />
          );
        })}
      </StackedBar>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
        {CATEGORY_ORDER.map((cat) => {
          const bytes = categoryTotals.get(cat) ?? 0;
          if (bytes === 0) return null;
          return (
            <div key={cat} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10 }}>
              <Dot color={CATEGORY_COLOR[cat]} />
              <span style={{ color: theme.colors.textMuted }}>{CATEGORY_LABEL[cat]}</span>
              <span style={{ color: theme.colors.text, fontFamily: "monospace" }}>
                {formatBytes(bytes)}
              </span>
            </div>
          );
        })}
        <div style={{ marginLeft: "auto", fontSize: 10, color: theme.colors.textMuted }}>
          Total: <span style={{ color: theme.colors.text, fontFamily: "monospace" }}>{formatBytes(totalBytes)}</span>
        </div>
      </div>

      {/* Per-table breakdown */}
      <TableGrid>
        <GridHeader>Table</GridHeader>
        <GridHeader>Data</GridHeader>
        <GridHeader>Indexes</GridHeader>
        <GridHeader>Total</GridHeader>
        <GridHeader></GridHeader>

        {CATEGORY_ORDER.map((cat) => {
          const rows = byCategory.get(cat) ?? [];
          if (rows.length === 0) return null;
          const catTotal = categoryTotals.get(cat) ?? 0;
          return [
            <GridSectionHeader key={`header-${cat}`}>
              {CATEGORY_LABEL[cat]} — {formatBytes(catTotal)} ({pct(catTotal, totalBytes)})
            </GridSectionHeader>,
            ...rows.map((t) => {
              const isArchival = t.category === "transactions_archival";
              const isProcessed = t.category === "transactions_processed";
              return [
                <GridCell key={`${t.tableName}-name`}>
                  <Dot color={CATEGORY_COLOR[cat]} />
                  <span style={{ color: theme.colors.text }}>{t.tableName}</span>
                  {isArchival && <Tag variant="archival">archival</Tag>}
                  {isProcessed && <Tag variant="operational">operational</Tag>}
                </GridCell>,
                <GridCell key={`${t.tableName}-data`} right muted>
                  {formatBytes(t.dataBytes)}
                </GridCell>,
                <GridCell key={`${t.tableName}-idx`} right muted>
                  {t.indexBytes > 0 ? formatBytes(t.indexBytes) : "—"}
                </GridCell>,
                <GridCell key={`${t.tableName}-total`} right>
                  {formatBytes(t.totalBytes)}
                </GridCell>,
                <GridCell key={`${t.tableName}-bar`} right>
                  <InlineBar
                    widthPct={totalBytes > 0 ? (t.totalBytes / totalBytes) * 100 : 0}
                    color={CATEGORY_COLOR[cat]}
                  />
                </GridCell>,
              ];
            }),
          ];
        })}
      </TableGrid>

      <Freshness>
        Snapshot at {new Date(generatedAt).toLocaleTimeString()}
      </Freshness>
    </div>
  );
}
